import React, { useState, useEffect } from "react";
// API requests
import { updateClient } from "api/requests/updateClient";
import { getTaskList, addTaskToProject } from "api/requests/zohoRequests";
// Auth
import useAuth from "hooks/useAuth";
// Chakra imports
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Input,
  InputGroup,
  InputLeftAddon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  Tag,
  TagLabel,
  TagCloseButton,
  Text,
  Textarea,
  Tooltip,
  useColorModeValue,
  useToast,
  ModalHeader,
  Wrap,
  ButtonGroup,
  Spacer,
} from "@chakra-ui/react";
// Icons
import { InfoToolTip } from "components/Icons/Icons.js";
// Custom components
import ClientToggle from "./ClientToggle";

function ClientModal({ props, onClose, isOpen, onUpdate, refresh }) {
  const client = props;
  const { hasRequiredRole } = useAuth();
  const [resize, setResize] = useState("vertical");

  const [scrollBehavior, setScrollBehavior] = React.useState("inside");

  const toast = useToast();

  // form states
  const [clientName, setClientName] = useState();
  const [clientWebsite, setClientWebsite] = useState();
  const [brand1, setBrand1] = useState();
  const [projectID, setProjectID] = useState();
  const [newUrl, setNewUrl] = useState();
  const [usedUrl, setUsedUrl] = useState();
  const [vytUrl, setVytUrl] = useState();
  const [financeUrl, setFinanceUrl] = useState();
  const [specialsUrl, setSpecialsUrl] = useState();
  const [additionalText, setAdditionalText] = useState();
  // colors
  const bgColor = useColorModeValue("white", "#4A5058");
  const textColor = useColorModeValue("gray.600", "white");
  // Recurring Task Lists
  const [zohoTaskLists, setZohoTaskLists] = useState([]);
  const [clientRecurringProducts, setClientRecurringProducts] = useState([]);
  const [recurringTaskLists, setRecurringTaskLists] = useState([]);

  // Checkboxes
  const [checkboxState, setCheckboxState] = useState();
  const [taskIds, setTaskIds] = useState({
    coop: {
      id: "1290214000009704891",
    },
    creativeBanners: {
      id: "1290214000007226478",
    },
    creativeEblast: {
      id: "1290214000007952400",
    },
    ga4: {
      id: "1290214000007226518",
    },
    googleDisplay: {
      id: "1290214000007226630",
    },
    googleVla: {
      id: "1290214000007226646",
    },
    paidSearch: {
      id: "1290214000007226766",
    },
    paidSocial: {
      id: "1290214000007226678",
    },
    serviceAdPackage: {
      id: "1290214000007226422",
    },
    techSeo: {
      id: "1290214000007761131",
    },
    websiteManagement: {
      id: "1290214000007226879",
    },
    coreSeo: {
      id: "1290214000007522127",
    },
    enhancedSeo: {
      id: "1290214000007761131",
    },
    premiumSeo: {
      id: "1290214000007522137",
    },
  });

  const handleReset = () => {
    setClientName();
    setClientWebsite();
    setBrand1();
    setNewUrl();
    setUsedUrl();
    setVytUrl();
    setFinanceUrl();
    setSpecialsUrl();
    setAdditionalText();
    setProjectID();
    setRecurringTaskLists([]);
  };

  let defUrls = [
    client?.navbar?.newvehicle,
    client?.navbar?.usedvehicle,
    client?.navbar?.additional,
    client?.navbar?.finance,
  ];

  const [clientData, setClientData] = useState({});
  const [originalData, setOriginalData] = useState({});

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;

    // Determine if the input type is a checkbox to use `checked`, otherwise use `value`
    const inputValue = type === "checkbox" ? checked : value;

    // Handle updates for the label specially
    if (name === "label") {
      setClientData((prev) => ({
        ...prev,
        label: inputValue,
        value: inputValue.toString().replace(/\W/g, ""), // Sanitizing input for `value`
      }));
    } else {
      // Split the name to handle nested fields, e.g., "products.paidSearch.status"
      const keys = name.split(".");

      if (keys.length === 3) {
        // Handle three levels of nesting
        setClientData((prev) => ({
          ...prev,
          [keys[0]]: {
            ...prev[keys[0]],
            [keys[1]]: {
              ...prev[keys[0]][keys[1]],
              [keys[2]]: inputValue,
            },
          },
        }));
      } else if (keys.length === 2) {
        // Handle two levels of nesting
        setClientData((prev) => ({
          ...prev,
          [keys[0]]: {
            ...prev[keys[0]],
            [keys[1]]: inputValue,
          },
        }));
      } else {
        // Handle non-nested fields
        setClientData((prev) => ({ ...prev, [name]: inputValue }));
      }
    }
  };

  const saveChanges = async () => {
    const updatedFields = {};
    Object.keys(clientData).forEach((key) => {
      if (clientData[key] !== originalData[key]) {
        updatedFields[key] = clientData[key];
      }
    });
    const response = await updateClient(updatedFields, client._id);
    if (response.status === 200) {
      toast({
        title: "Success!",
        description: "Client updates saved",
        status: "success",
        duration: 2000,
      });
      refresh ? await refresh() : null;
    } else {
      toast({
        title: "Error!",
        description: `Status code: ${response.status}`,
        status: "error",
        duration: 2000,
      });
    }
  };

  useEffect(() => {
    // Set initial states from client
    if (client) {
      setCheckboxState(client?.products || {});
      setClientData(client);
      setOriginalData({ ...client });

      // Initialize recurring tasks based on products' status and task IDs
      const initialRecurringTasks = Object.entries(
        client.products || {}
      ).reduce((acc, [productName, productDetails]) => {
        if (productDetails.status) {
          // Check if the product's status is true
          const taskId = taskIds[productName]?.id;
          if (taskId) {
            // Ensure there is a corresponding task ID
            const matchingTask = zohoTaskLists.find(
              (task) => task.id === taskId
            );
            if (matchingTask) {
              acc.push({
                id: matchingTask.id,
                name: matchingTask.name, // Use the name from the zohoTaskLists
                status: true, // Assuming you want to mark these tasks as active
              });
            }
          }
        }
        return acc;
      }, []);

      // Set the recurring tasks state
      setRecurringTaskLists([...recurringTaskLists, ...initialRecurringTasks]);
    }
  }, [client, zohoTaskLists]);

  function camelCaseToTitleCase(camelCase) {
    const exceptions = ["SEO", "GA4", "AI", "VLA"]; // Existing exceptions
    const specificTransformations = { coop: "CO-OP" }; // New specific transformations

    if (camelCase == null || camelCase === "") {
      return camelCase;
    }

    camelCase = camelCase.trim();
    let newText = "";
    for (let i = 0; i < camelCase.length; i++) {
      if (
        /[A-Z]/.test(camelCase[i]) &&
        i !== 0 &&
        /[a-z]/.test(camelCase[i - 1])
      ) {
        newText += " ";
      }
      newText += camelCase[i];
    }

    // Split the text and capitalize the first letter of each word
    let titleCaseText = newText
      .split(" ")
      .map((word) => {
        // Check if the word should be transformed specifically
        const upperWord = word.toUpperCase();
        if (specificTransformations[word.toLowerCase()]) {
          return specificTransformations[word.toLowerCase()];
        }
        // Check if the word is an exception, and format accordingly
        if (exceptions.includes(upperWord)) {
          return upperWord;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");

    return titleCaseText;
  }

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    setCheckboxState((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        status: checked,
      },
    }));

    const taskId = taskIds[name].id;
    const taskListUpdate = zohoTaskLists.find((task) => task.id === taskId);

    if (taskListUpdate && checked) {
      const updatedTask = { ...taskListUpdate, status: true };
      setRecurringTaskLists((prev) => {
        const exists = prev.some((item) => item.id === taskId);
        return exists ? prev : [...prev, updatedTask];
      });
    } else if (taskListUpdate && !checked) {
      setRecurringTaskLists((prev) =>
        prev.filter((item) => item.id !== taskId)
      );
    }
    setClientData((prev) => ({
      ...prev,
      products: {
        ...prev.products,
        [name]: {
          status: checked,
        },
      },
    }));
  };

  // Generate checkbox components from the state
  const skipKeys = [
    "automatedSpecials",
    "aiCoupon",
    "spanishAdPackage",
    "contentSeo",
    "ott",
  ];
  const checkboxes = checkboxState
    ? Object.entries(checkboxState)
        .filter(([key, value]) => !skipKeys.includes(key)) // Filter out the keys to skip
        .map(([key, value]) => (
          <Checkbox
            key={key}
            name={key}
            isChecked={value.status}
            onChange={handleCheckboxChange}
            colorScheme="green"
            color={textColor}
          >
            {camelCaseToTitleCase(key.split(".").pop())}
          </Checkbox>
        ))
    : null;

  const fetchTaskLists = async () => {
    try {
      const data = await getTaskList();
      const updatedTaskLists = data.task_list_templates.map((taskList) => ({
        id: taskList.id,
        name: taskList.name,
        status: false,
      }));
      setZohoTaskLists([...updatedTaskLists]);
    } catch (error) {
      console.error("Failed to fetch Task Lists:", error);
    }
  };

  useEffect(() => {
    fetchTaskLists();
  }, []);

  const handleAddTasks = async () => {
    const data = {
      id: client?.projectId,
      taskLists: recurringTaskLists.map((tasklist) => ({
        id: tasklist.id,
      })),
      clientName: client?.label,
    };
    try {
      const response = await addTaskToProject(data);
    } catch (error) {
      console.error("Failed to add Tasks to Project:", error);
    }
  };

  useEffect(() => {
    if (client?.projectId) {
      setProjectID(client.projectId);
    }
  }, [client?.projectId]);

  const canViewSection = hasRequiredRole([
    "Admin",
    "SuperAdmin",
    "Rep",
    "Manager",
  ]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        motionPreset="slideInBottom"
        size="6xl"
        scrollBehavior={scrollBehavior}
      >
        <ModalOverlay />
        <ModalContent p={6} bg={bgColor}>
          <ModalHeader>
            <Heading m="20px">
              <Flex direction="column" alignItems="center">
                <Text
                  color={textColor}
                  fontSize="lg"
                  fontWeight="bold"
                  mb="3px"
                >
                  Client Info
                </Text>
                <Text
                  color="gray.400"
                  fontWeight="normal"
                  fontSize="sm"
                  mb="10px"
                >
                  Mandatory Information
                </Text>
                <Badge
                  variant="solid"
                  fontSize="0.5em"
                  backgroundColor={
                    client?.status === true ? "green.500" : "yellow.400"
                  }
                >
                  {client?.status === true ? "Active" : "Inactive"}
                </Badge>
              </Flex>
            </Heading>
          </ModalHeader>
          <ModalBody>
            <Flex direction="column" w="100%">
              <Stack direction="column" spacing="20px">
                <Stack direction="row" spacing={{ sm: "24px", lg: "30px" }}>
                  <FormControl>
                    <FormLabel
                      color={textColor}
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      Client Name
                    </FormLabel>
                    <Input
                      variant="main"
                      fontSize="xs"
                      value={
                        clientName !== undefined
                          ? clientName
                          : client?.label || ""
                      }
                      name="label"
                      onChange={(e) => (
                        setClientName((prevState) => e.target.value),
                        handleInputChange(e)
                      )}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel
                      color={textColor}
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      Client URL
                    </FormLabel>
                    <Input
                      variant="main"
                      name="clientwebsite"
                      value={
                        clientWebsite !== undefined
                          ? clientWebsite
                          : client?.clientwebsite || ""
                      }
                      fontSize="xs"
                      onChange={(e) => (
                        setClientWebsite((prevState) => e.target.value),
                        handleInputChange(e)
                      )}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel
                      color={textColor}
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      Brand
                    </FormLabel>
                    <Input
                      variant="main"
                      name="brand"
                      value={
                        brand1 !== undefined ? brand1 : client?.brand || ""
                      }
                      fontSize="xs"
                      onChange={(e) => (
                        setBrand1((prevState) => e.target.value),
                        handleInputChange(e)
                      )}
                    />
                  </FormControl>
                </Stack>
                {!canViewSection ? null : (
                  <>
                    <Heading>
                      <Flex direction="column">
                        <Text
                          color={textColor}
                          fontSize="lg"
                          fontWeight="bold"
                          mb="3px"
                        >
                          Client Status
                        </Text>
                      </Flex>
                    </Heading>
                    <ClientToggle
                      isActive={clientData.status === true}
                      onToggle={(e) => handleInputChange(e)}
                    />
                    <Heading>
                      <Flex direction="column">
                        <Text
                          color={textColor}
                          fontSize="lg"
                          fontWeight="bold"
                          mb="3px"
                        >
                          Products (Recurring)
                        </Text>
                      </Flex>
                    </Heading>
                    <Grid templateColumns="repeat(5, 1fr)" gap={2}>
                      {checkboxes}
                    </Grid>
                    <Heading size="sm" pt={3} color={textColor}>
                      Recurring Tasklists:
                    </Heading>
                    <Box
                      border={"1px"}
                      borderColor={"gray.200"}
                      borderRadius={"md"}
                      my={5}
                      p={2}
                    >
                      <Wrap spacing={2}>
                        {recurringTaskLists.map((taskList) => (
                          <Tag
                            size={"md"}
                            key={taskList.id}
                            borderRadius={"md"}
                            variant={"solid"}
                            colorScheme={"blue"}
                          >
                            <TagLabel>{taskList.name}</TagLabel>
                          </Tag>
                        ))}
                      </Wrap>
                    </Box>
                    <Flex align={"center"} gap={2}>
                      <InputGroup size={"sm"} w={"fit-content"}>
                        <InputLeftAddon>Project ID:</InputLeftAddon>
                        <Input
                          type="number"
                          name="projectId"
                          placeholder="No Project Saved"
                          value={projectID || ""}
                          color={textColor}
                          onChange={(e) => {
                            setProjectID(e.target.value);
                            handleInputChange(e);
                          }}
                        />
                      </InputGroup>
                      <Spacer />
                      <ButtonGroup>
                        <Button onClick={() => handleAddTasks()}>
                          Load Product Tasks
                        </Button>
                        <Button onClick={() => console.log(checkboxState)}>
                          Task Manager
                        </Button>
                      </ButtonGroup>
                    </Flex>
                  </>
                )}
                <Heading>
                  <Flex direction="column">
                    <Text
                      color={textColor}
                      fontSize="lg"
                      fontWeight="bold"
                      mb="3px"
                    >
                      S3 Info
                    </Text>
                  </Flex>
                </Heading>
                <Stack direction="column" spacing={3}>
                  <InputGroup>
                    <InputLeftAddon children="Bucket" />
                    <Input
                      variant="main"
                      type="url"
                      defaultValue="da-client-assets"
                      fontSize="xs"
                      disabled
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLeftAddon children="Directory" />
                    <Input
                      variant="main"
                      type="url"
                      defaultValue={
                        !client?.s3FolderPath
                          ? "No Directory currently created."
                          : client?.s3FolderPath
                      }
                      fontSize="xs"
                      disabled
                    />
                  </InputGroup>
                </Stack>
                <Heading>
                  <Flex direction="column">
                    <Text
                      color={textColor}
                      fontSize="lg"
                      fontWeight="bold"
                      mb="3px"
                    >
                      Links
                    </Text>
                    <Text color="gray.400" fontWeight="normal" fontSize="sm">
                      Links to be used as defaults
                    </Text>
                  </Flex>
                </Heading>
                <Stack direction="row" spacing={5} w="100%">
                  <Box w="100%">
                    <Stack direction="column" spacing={2}>
                      <Stack direction="row" spacing={3}>
                        <Heading
                          color={textColor}
                          fontWeight="bold"
                          fontSize="sm"
                        >
                          NavBar New SRP URL
                        </Heading>
                        <Tooltip
                          hasArrow
                          label="Navbar New Vehicle Image and New Vehicle URL"
                          fontSize="md"
                        >
                          <InfoToolTip />
                        </Tooltip>
                      </Stack>
                      <Stack spacing={3}>
                        <InputGroup>
                          <InputLeftAddon children="Link" />
                          <Input
                            variant="main"
                            placeholder="No Link Saved"
                            type="url"
                            name="navbar.newvehicle"
                            value={
                              newUrl !== undefined
                                ? newUrl
                                : client?.navbar?.newvehicle || ""
                            }
                            fontSize="xs"
                            onChange={(e) => (
                              setNewUrl((prevState) => e.target.value),
                              handleInputChange(e)
                            )}
                          />
                        </InputGroup>
                      </Stack>
                    </Stack>
                  </Box>
                  <Box w="100%">
                    <Stack direction="column" spacing={2}>
                      <Stack direction="row" spacing={3}>
                        <Heading
                          color={textColor}
                          fontWeight="bold"
                          fontSize="sm"
                        >
                          NavBar Used SRP URL
                        </Heading>
                        <Tooltip
                          hasArrow
                          label="Navbar Used Vehicle Image and Used Vehicle URL"
                          fontSize="md"
                        >
                          <InfoToolTip />
                        </Tooltip>
                      </Stack>
                      <Stack spacing={3}>
                        <InputGroup>
                          <InputLeftAddon children="Link" />
                          <Input
                            variant="main"
                            placeholder="No Link Saved"
                            type="url"
                            name="navbar.usedvehicle"
                            value={
                              usedUrl !== undefined
                                ? usedUrl
                                : client?.navbar?.usedvehicle || ""
                            }
                            fontSize="xs"
                            onChange={(e) => (
                              setUsedUrl((prevState) => e.target.value),
                              handleInputChange(e)
                            )}
                          />
                        </InputGroup>
                      </Stack>
                    </Stack>
                  </Box>
                  <Box w="100%">
                    <Stack direction="column" spacing={2}>
                      <Stack direction="row" spacing={3}>
                        <Heading
                          color={textColor}
                          fontWeight="bold"
                          fontSize="sm"
                        >
                          NavBar VYT SRP URL
                        </Heading>
                        <Tooltip
                          hasArrow
                          label="Navbar Value Your Trade URL"
                          fontSize="md"
                        >
                          <InfoToolTip />
                        </Tooltip>
                      </Stack>
                      <Stack spacing={3}>
                        <InputGroup>
                          <InputLeftAddon children="Link" />
                          <Input
                            variant="main"
                            placeholder="No Link Saved"
                            type="url"
                            name="navbar.additional"
                            value={
                              vytUrl !== undefined
                                ? vytUrl
                                : client?.navbar?.additional || ""
                            }
                            fontSize="xs"
                            onChange={(e) => (
                              setVytUrl((prevState) => e.target.value),
                              handleInputChange(e)
                            )}
                          />
                        </InputGroup>
                      </Stack>
                    </Stack>
                  </Box>
                  <Box w="100%">
                    <Stack direction="column" spacing={2}>
                      <Stack direction="row" spacing={3}>
                        <Heading
                          color={textColor}
                          fontWeight="bold"
                          fontSize="sm"
                        >
                          NavBar Finance URL
                        </Heading>
                        <Tooltip
                          hasArrow
                          label="Navbar Finance Image and Finance URL"
                          fontSize="md"
                        >
                          <InfoToolTip />
                        </Tooltip>
                      </Stack>
                      <Stack spacing={3}>
                        <InputGroup>
                          <InputLeftAddon children="Link" />
                          <Input
                            variant="main"
                            placeholder="No Link Saved"
                            type="url"
                            name="navbar.finance"
                            value={
                              financeUrl !== undefined
                                ? financeUrl
                                : client?.navbar?.finance || ""
                            }
                            fontSize="xs"
                            onChange={(e) => (
                              setFinanceUrl((prevState) => e.target.value),
                              handleInputChange(e)
                            )}
                          />
                        </InputGroup>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
                <Stack direction="column" spacing={3}>
                  <Stack direction={["column", "row"]} spacing={3}>
                    <Heading color={textColor} fontWeight="bold" fontSize="md">
                      Specials URL
                    </Heading>
                    <Tooltip
                      hasArrow
                      label="New or Used Specials Image and URL"
                      fontSize="md"
                    >
                      <InfoToolTip />
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={3}>
                    <Flex direction="column" w="100%" gap="3">
                      <InputGroup>
                        <InputLeftAddon children="Link" />
                        <Input
                          variant="main"
                          placeholder="No Link Saved"
                          type="url"
                          name="specials"
                          value={
                            specialsUrl !== undefined
                              ? specialsUrl
                              : client?.specials || ""
                          }
                          fontSize="xs"
                          onChange={(e) => (
                            setSpecialsUrl((prevState) => e.target.value),
                            handleInputChange(e)
                          )}
                        />
                      </InputGroup>
                    </Flex>
                  </Stack>
                </Stack>
                <Stack direction="column" spacing={3}>
                  <Stack direction={["column", "row"]} spacing={3}>
                    <Heading color={textColor} fontWeight="bold" fontSize="md">
                      Additional Text / Privacy Policy
                    </Heading>
                    <Tooltip
                      hasArrow
                      label="Additional Text / Privacy Policy that should appear at the bottom of the Email Blast"
                      fontSize="md"
                    >
                      <InfoToolTip />
                    </Tooltip>
                  </Stack>
                  <Textarea
                    name="additionaltext"
                    value={
                      additionalText !== undefined
                        ? additionalText
                        : client?.additionaltext || ""
                    }
                    size="sm"
                    resize={resize}
                    color={textColor}
                    onChange={(e) => (
                      setAdditionalText((prevState) => e.target.value),
                      handleInputChange(e)
                    )}
                  />
                </Stack>
              </Stack>
            </Flex>
          </ModalBody>
          <ModalCloseButton onClick={() => handleReset()} />
          <ModalFooter>
            {/* <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                console.log(clientData);
              }}
            >
              Client
            </Button> */}
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                saveChanges();
              }}
            >
              Save
            </Button>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                handleReset();
                onClose();
              }}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default ClientModal;
